import { useRef, useState } from 'react'
import { ApplicationRoutes, useCurrentRootPath } from '~/hooks/useRouting'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { isElectron } from '~/utils/common'
import { addMessageToQueue } from '~/utils/popupQueueUtils'

import {
  POPUP_DEFAULT_HEIGHT,
  POPUP_DEFAULT_WIDTH,
  POPUP_HEADER_HEIGHT,
  POPUP_MIN_HEIGHT,
} from '@mangovoice/constants'
import { MessageQueueItem, MessageThread } from '@mangovoice/types'

const usePopupWindow = () => {
  const currentRoot = useCurrentRootPath()

  const { isMainWindowFocused } = useMainWindow()
  const [messageQueue, setMessageQueue] =
    useSharedVariable<MessageQueueItem[]>('MessageQueue')

  const showPopupWindow = () => {
    window.electron?.send('show-popup-window')
  }

  const hidePopupWindow = () => {
    window.electron?.send('hide-popup-window')
  }

  const getPopupWindowHeight = async (): Promise<number | null> => {
    try {
      return await window.electron?.invoke('get-popup-window-height')
    } catch (error) {
      console.warn('Failed to get current popup height:', error)
      return null
    }
  }

  const changePopupDimensions = (width: number, height: number) => {
    window.electron?.send('change-popup-dimensions', { width, height })
  }

  const addTextToPopupQueue = (message: MessageThread[]) => {
    const isMessagingRoute = currentRoot === ApplicationRoutes.MESSAGING

    const doAction = () => {
      setMessageQueue(addMessageToQueue(messageQueue || [], message))
      showPopupWindow()
    }

    if (message) {
      if (message?.length) {
        isMainWindowFocused()?.then((isFocused: boolean) => {
          if (isMessagingRoute) {
            if (!isFocused) {
              doAction()
            }
          } else {
            doAction()
          }
        })
      }
    }
  }

  return {
    hidePopupWindow,
    showPopupWindow,
    addTextToPopupQueue,
    getPopupWindowHeight,
    changePopupDimensions,
  }
}

const usePopupMinimize = () => {
  const [isMinimized, setIsMinimized] = useState(false)
  const previousHeightRef = useRef<number>(POPUP_DEFAULT_HEIGHT)
  const { getPopupWindowHeight, changePopupDimensions } = usePopupWindow()

  const toggleMinimize = async () => {
    if (isMinimized) {
      changePopupDimensions(POPUP_DEFAULT_WIDTH, previousHeightRef.current)
      setIsMinimized(false)
    } else {
      const currentHeight = await getPopupWindowHeight()
      if (currentHeight) {
        previousHeightRef.current =
          currentHeight > POPUP_MIN_HEIGHT ? currentHeight : POPUP_MIN_HEIGHT
      } else {
        previousHeightRef.current = POPUP_DEFAULT_HEIGHT
      }

      changePopupDimensions(POPUP_DEFAULT_WIDTH, POPUP_HEADER_HEIGHT)
      setIsMinimized(true)
    }
  }

  return { isMinimized, toggleMinimize }
}

const useMainWindow = () => {
  const focusMainWindow = (postFocusEvent?: string, data?: unknown) => {
    if (isElectron) {
      window.electron?.send('focus-main-window', postFocusEvent, data)
    }
  }

  const isMainWindowFocused = (): Promise<any> | undefined => {
    if (isElectron) {
      return window.electron
        ?.invoke('get-main-is-focused')
        .then((isFocused) => isFocused)
    } else {
      return new Promise((resolve) => resolve(true))
    }
  }

  const answerPhoneCall = (callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-answer-call', callUuid)
    }
  }

  const answerCallWithEnd = (callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-answer-call-with-end', callUuid)
    }
  }

  const answerCallWithHold = (callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-answer-call-with-hold', callUuid)
    }
  }

  const rejectPhoneCall = (callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-reject-call', callUuid)
    }
  }

  const parkCall = (parkTarget: string, callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-park-call', parkTarget, callUuid)
    }
  }

  const answerCallWithParkCurrent = (parkTarget: string, callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-answer-call-with-park', parkTarget, callUuid)
    }
  }

  const holdCall = (callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-hold-call', callUuid)
    }
  }

  const resumeCall = (callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-resume-call', callUuid)
    }
  }

  const endCall = (callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-end-call', callUuid)
    }
  }

  const switchCall = (callUuid?: string) => {
    if (isElectron) {
      window.electron?.send('popup-switch-call', callUuid)
    }
  }

  const dismissCall = (callUuid: string) => {
    if (isElectron) {
      window.electron?.send('popup-dismiss-call', callUuid)
    }
  }

  return {
    focusMainWindow,
    isMainWindowFocused,
    answerPhoneCall,
    rejectPhoneCall,
    answerCallWithEnd,
    answerCallWithHold,
    answerCallWithParkCurrent,
    parkCall,
    holdCall,
    resumeCall,
    endCall,
    switchCall,
    dismissCall,
  }
}

export { usePopupWindow, useMainWindow, usePopupMinimize }
